import React, { memo, useState, useEffect, useRef, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import useSimulationStore from '../../../store/simulationStore';

interface SpeciesPercentagesChartProps {
    height?: number;
}

// Color palette
const COLORS = [
    '#4fc3f7', '#81c784', '#ffb74d', '#f48fb1', '#ce93d8',
    '#90caf9', '#a5d6a7', '#ffe082', '#ef9a9a', '#b39ddb'
];

const MAX_DATA_POINTS = 60;
const UPDATE_INTERVAL = 500;

interface DataPoint {
    time: number;
    [species: string]: number;
}

const SpeciesPercentagesChart: React.FC<SpeciesPercentagesChartProps> = memo(({ height = 250 }) => {
    const [history, setHistory] = useState<DataPoint[]>([]);
    const timeRef = useRef(0);
    const lastUpdateRef = useRef(0);

    const { speciesCounts, isRunning } = useSimulationStore(
        state => ({
            speciesCounts: state.speciesCounts,
            isRunning: state.isRunning,
        }),
        shallow
    );

    const speciesNames = useMemo(() => Object.keys(speciesCounts), [speciesCounts]);

    // Throttled update for time-series
    useEffect(() => {
        if (!isRunning) return;

        const interval = setInterval(() => {
            const now = Date.now();
            if (now - lastUpdateRef.current < UPDATE_INTERVAL) return;
            lastUpdateRef.current = now;

            timeRef.current += UPDATE_INTERVAL / 1000;

            // Convert counts to percentages
            const total = Object.values(speciesCounts).reduce((sum, c) => sum + c, 0);
            const percentages: DataPoint = { time: timeRef.current };

            Object.entries(speciesCounts).forEach(([name, count]) => {
                percentages[name] = total > 0 ? (count / total) * 100 : 0;
            });

            setHistory(prev => {
                const updated = [...prev, percentages];
                return updated.length > MAX_DATA_POINTS
                    ? updated.slice(-MAX_DATA_POINTS)
                    : updated;
            });
        }, UPDATE_INTERVAL);

        return () => clearInterval(interval);
    }, [isRunning, speciesCounts]);

    // Reset on simulation reset
    useEffect(() => {
        if (!isRunning && history.length > 0) {
            const totalCount = Object.values(speciesCounts).reduce((sum, c) => sum + c, 0);
            if (totalCount === 0) {
                setHistory([]);
                timeRef.current = 0;
            }
        }
    }, [isRunning, speciesCounts, history.length]);

    // Current percentages for summary
    const currentPercentages = useMemo(() => {
        const total = Object.values(speciesCounts).reduce((sum, c) => sum + c, 0);
        return Object.entries(speciesCounts).map(([name, count]) => ({
            name,
            percentage: total > 0 ? ((count / total) * 100).toFixed(1) : '0'
        }));
    }, [speciesCounts]);

    const totalParticles = useMemo(() =>
        Object.values(speciesCounts).reduce((sum, count) => sum + count, 0),
        [speciesCounts]
    );

    return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Summary */}
            <div style={summaryStyle}>
                <span>Total: <strong style={{ color: '#4fc3f7' }}>{totalParticles}</strong></span>
                <span>Species: <strong style={{ color: '#81c784' }}>{speciesNames.length}</strong></span>
            </div>

            {/* Current percentages */}
            <div style={percentageBarStyle}>
                {currentPercentages.map((item, index) => (
                    <span key={item.name} style={{ color: COLORS[index % COLORS.length] }}>
                        {item.name}: {item.percentage}%
                    </span>
                ))}
            </div>

            {/* Chart */}
            <div style={{ flex: 1, minHeight: 0 }}>
                {history.length > 1 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={history} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                            <XAxis
                                dataKey="time"
                                stroke="#888"
                                tick={{ fill: '#ccc', fontSize: 10 }}
                                tickFormatter={(v) => `${v.toFixed(0)}s`}
                            />
                            <YAxis
                                stroke="#888"
                                tick={{ fill: '#ccc', fontSize: 10 }}
                                domain={[0, 100]}
                                tickFormatter={(v) => `${v}%`}
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: 'rgba(30, 30, 30, 0.95)',
                                    border: '1px solid #4fc3f7',
                                    borderRadius: '4px',
                                    color: '#fff'
                                }}
                                formatter={(value: number) => `${value.toFixed(1)}%`}
                                labelFormatter={(label) => `Time: ${Number(label).toFixed(1)}s`}
                            />
                            <Legend wrapperStyle={{ fontSize: '10px' }} />
                            {speciesNames.map((species, index) => (
                                <Area
                                    key={species}
                                    type="monotone"
                                    dataKey={species}
                                    stackId="1"
                                    stroke={COLORS[index % COLORS.length]}
                                    fill={COLORS[index % COLORS.length]}
                                    fillOpacity={0.6}
                                    isAnimationActive={false}
                                />
                            ))}
                        </AreaChart>
                    </ResponsiveContainer>
                ) : (
                    <div style={emptyStateStyle}>
                        <span>Collecting data...</span>
                        <span style={{ fontSize: '10px', color: '#666' }}>Start simulation to see percentages over time</span>
                    </div>
                )}
            </div>
        </div>
    );
});

SpeciesPercentagesChart.displayName = 'SpeciesPercentagesChart';

const summaryStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 12px',
    backgroundColor: 'rgba(40, 40, 40, 0.5)',
    borderRadius: '4px',
    marginBottom: '4px',
    fontSize: '11px',
    color: '#ccc',
    fontFamily: 'monospace',
};

const percentageBarStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    padding: '4px 12px',
    fontSize: '10px',
    fontFamily: 'monospace',
    marginBottom: '8px',
};

const emptyStateStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#888',
    fontSize: '12px',
    gap: '8px',
};

export default SpeciesPercentagesChart;
